import { useState } from 'react';

import { TwoFactorCard } from '@/components/TwoFactorCard';
import { passkeyMessage, type PasskeyFailure } from '@/lib/auth';
import { passwordProblemMessage, useChangePassword } from '@/lib/profile';
import {
  deviceLabel,
  lastUsed,
  useDeletePasskey,
  useMyPasskeys,
  useMySessions,
  useRegisterPasskey,
  useRevokeOtherSessions,
  useRevokeSession,
} from '@/lib/passkeys';

/**
 * How somebody signs in to this admin.
 *
 * Passkeys, the password, and where the account is signed in — the three
 * things that decide who can get in.
 */
export default function SecurityPage() {
  const passkeys = useMyPasskeys();
  const sessions = useMySessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const register = useRegisterPasskey();
  const remove = useDeletePasskey();

  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmingSession, setConfirmingSession] = useState<string | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);

  const password = useChangePassword();
  const [changing, setChanging] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');

  const rows = passkeys.data ?? [];

  const add = () => {
    register.mutate(name.trim() || defaultName(), {
      onSuccess: () => {
        setName('');
        setAdding(false);
      },
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Passkeys</h2>
          {/*
            "0 registered", not "0". A bare number beside a heading is a figure
            without a unit — the design labels both of these counts, and this
            one is the difference between "no keys" and "nothing loaded yet".
          */}
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {rows.length} registered
          </span>
          {!adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
            >
              <span aria-hidden="true">＋</span>
              Add a passkey
            </button>
          ) : null}
        </span>

        <span className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--muted)' }}>
          Sign in with Touch ID, Windows Hello or a security key instead of typing a password. A
          passkey belongs to the device that made it, so add one on each device you use.
        </span>

        {passkeys.isPending ? (
          <p className="m-0 py-2 text-[13px]" style={{ color: 'var(--muted)' }}>
            Loading…
          </p>
        ) : null}

        {passkeys.isError ? (
          <p className="m-0 py-2 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            Could not load your passkeys. Reload the page to try again.
          </p>
        ) : null}

        {!passkeys.isPending && !passkeys.isError ? (
          <div data-rows>
            {rows.map((passkey) => (
              <div
                key={passkey.id}
                className="flex flex-wrap items-center gap-3.5 border-b py-[13px]"
                style={{ borderColor: 'var(--line)' }}
              >
                <span className="flex-[1_1_160px] text-sm">{passkey.name}</span>
                <span className="font-mono text-[11px]" style={{ color: 'var(--muted)' }}>
                  {lastUsed(passkey.lastUsedAt)}
                </span>
                {/*
                  Asked in place. `window.confirm` blocks the page and cannot be
                  driven in a test, and removing the key you are holding is not
                  a thing to do on one click.
                */}
                {confirming === passkey.id ? (
                  <span className="flex items-center gap-2.5">
                    <span className="text-[12.5px]">Remove it?</span>
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() =>
                        remove.mutate(passkey.id, { onSuccess: () => setConfirming(null) })
                      }
                      className="btn-accent focus-ring cursor-pointer px-3 py-1.5 text-[12.5px] disabled:cursor-default disabled:opacity-50"
                    >
                      {remove.isPending ? 'Removing…' : 'Yes, remove'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(null)}
                      className="btn-quiet focus-ring cursor-pointer text-[12.5px]"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Remove ${passkey.name}`}
                    onClick={() => setConfirming(passkey.id)}
                    className="btn-quiet focus-ring cursor-pointer text-[12.5px]"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            {rows.length === 0 ? (
              /*
                The design's dashed box with an amber dot, not a line of prose:
                "you have none, and something on another screen is waiting on
                it" is a state, and it reads as one.
              */
              <span
                className="flex items-center gap-2.5 rounded-lg border border-dashed px-[11px] py-[9px]"
                style={{ background: 'var(--in)', borderColor: 'var(--line2)' }}
              >
                <span
                  aria-hidden="true"
                  className="h-[5px] w-[5px] flex-none rounded-full"
                  style={{ background: 'var(--warn)' }}
                />
                <span className="text-[11.5px]" style={{ color: 'var(--dim)' }}>
                  No passkeys yet — the passkey button on the sign-in screen has nothing to use.
                </span>
              </span>
            ) : null}
          </div>
        ) : null}

        {adding ? (
          /*
            ⚠️ `items-end`, and the buttons the field's own 32px. The row was
            `items-center`, which centres each button against the *label and
            box together* — so Continue floated half a line above the box's
            baseline, the nearest thing the eye lines it up with. Bottom-aligned,
            everything sits on the one line the box draws.
          */
          <div className="flex flex-wrap items-end gap-3 pt-1.5">
            <label className="flex flex-[1_1_220px] flex-col gap-1.5">
              <span className="field-label">What to call this key</span>
              <input
                value={name}
                autoFocus
                placeholder={defaultName()}
                onChange={(event) => setName(event.target.value)}
                className="field"
              />
            </label>
            <button
              type="button"
              disabled={register.isPending}
              onClick={add}
              className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
            >
              {register.isPending ? 'Waiting for the device…' : 'Continue'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="btn-quiet focus-ring min-h-8 cursor-pointer text-[12.5px]"
            >
              Cancel
            </button>
          </div>
        ) : null}

        {register.isError ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {registerMessage(register.error)}
          </p>
        ) : null}

        {remove.isError ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            Could not remove that passkey. Try again in a moment.
          </p>
        ) : null}
      </div>

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Password</h2>
          {/*
            ⚠️ Two lines the design draws here are missing, for two different
            reasons — and the difference matters to whoever reads this next.
            One is waiting; the other is settled.

            The mono "changed 14 Jun 2026" is **not coming** — anh Duy chốt
            [Settings 9]. Not blocked on a `passwordChangedAt` column: decided
            against. The date answers a narrow question — when did I last change
            it — while the question somebody opens this panel with is "has
            anyone been in my account", and the "Where you are signed in" list
            further down this screen already answers that one, device by device
            and with a way to end each of the others. A column, two write paths
            and a backfill, to print a line nobody would act on.

            "Two-factor authentication is on · authenticator app" is printed
            by `TwoFactorCard` below, and only once there is a factor to be on.
          */}
          {!changing ? (
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
            >
              Change password
            </button>
          ) : null}
        </span>

        {changing ? (
          <>
            {/*
              Said before it happens rather than reported after. Ending the
              other sessions is the point for the most common reason to be
              here — a suspicion that somebody else is signed in — and a person
              who did not expect it would think something had gone wrong.
            */}
            <p className="m-0 text-[12.5px] leading-[1.5]" style={{ color: 'var(--muted)' }}>
              Changing it signs out every other session. This one stays.
            </p>
            <div className="flex flex-wrap gap-3.5">
              <label className="flex flex-[1_1_200px] flex-col gap-1.5">
                <span className="field-label">Current password</span>
                <input
                  type="password"
                  value={current}
                  autoComplete="current-password"
                  onChange={(event) => setCurrent(event.target.value)}
                  className="field"
                />
              </label>
              <label className="flex flex-[1_1_200px] flex-col gap-1.5">
                <span className="field-label">New password</span>
                <input
                  type="password"
                  value={next}
                  autoComplete="new-password"
                  onChange={(event) => setNext(event.target.value)}
                  className="field"
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={password.isPending || current === '' || next === ''}
                onClick={() =>
                  password.mutate(
                    { currentPassword: current, newPassword: next },
                    {
                      onSuccess: (problem) => {
                        // Only on success. A refusal keeps what was typed, so
                        // fixing one field does not mean retyping both.
                        if (problem) return;
                        setCurrent('');
                        setNext('');
                        setChanging(false);
                      },
                    },
                  )
                }
                className="btn-accent focus-ring min-h-[38px] cursor-pointer px-4 py-2 text-[13.5px] disabled:cursor-default disabled:opacity-50"
              >
                {password.isPending ? 'Changing…' : 'Change it'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setChanging(false);
                  setCurrent('');
                  setNext('');
                }}
                className="btn-quiet focus-ring cursor-pointer text-[12.5px]"
              >
                Cancel
              </button>
            </div>
          </>
        ) : null}

        {password.data ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {passwordProblemMessage(password.data)}
          </p>
        ) : null}
        {password.isError ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            Could not change your password. Try again in a moment.
          </p>
        ) : null}
        {password.isSuccess && password.data === null && !changing ? (
          <p className="m-0 text-[12.5px]" style={{ color: 'var(--ok)' }}>
            Password changed. Every other session was signed out.
          </p>
        ) : null}
      </div>

      {/*
        Between the password and the sessions, where the design's one line
        about it sits — under the password, as the thing that comes after it.
      */}
      <TwoFactorCard />

      <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
        <span className="flex flex-wrap items-center gap-2.5">
          <h2 className="eyebrow m-0">Where you are signed in</h2>
          <span className="font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
            {sessions.data?.length ?? 0} active
          </span>
          {/*
            In the header, where the design puts it — an action over the whole
            list belongs beside the list's name, not below the last row where it
            reads as something to do with that row.
          */}
          {(sessions.data?.length ?? 0) > 1 && !confirmingAll ? (
            <button
              type="button"
              onClick={() => setConfirmingAll(true)}
              className="btn-small focus-ring ml-auto inline-flex cursor-pointer items-center gap-1"
            >
              Sign out everywhere else
            </button>
          ) : null}
        </span>

        {sessions.isError ? (
          <p className="m-0 py-2 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            Could not load your sessions. Reload the page to try again.
          </p>
        ) : null}

        <div data-rows>
          {(sessions.data ?? []).map((session) => (
            <div
              key={session.id}
              className="flex flex-wrap items-center gap-2.5 border-b py-2"
              style={{ borderColor: 'var(--line)' }}
            >
              <span className="flex min-w-0 flex-[1_1_160px] items-baseline gap-2">
                <span className="text-[12.5px]">{deviceLabel(session.device)}</span>
                {session.current ? (
                  /*
                    Lower case and untracked, as the design sets it. It was
                    uppercase with wide tracking, which is the v1 tag style —
                    and beside a device name it read as a status badge rather
                    than as "this is the one you are reading on".
                  */
                  <span
                    className="font-mono text-[10px] tracking-normal"
                    style={{ color: 'var(--accent)' }}
                  >
                    this session
                  </span>
                ) : null}
              </span>
              <span className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
                {session.ipAddress ?? 'no address'} · {lastUsed(session.lastUsedAt)}
              </span>
              {/*
                No sign-out button on the current session. Signing yourself out
                already has a place in the header, and a second one here — in a
                list whose purpose is ending *other* people's access — reads as
                the wrong thing at the moment somebody is worried.
              */}
              {session.current ? null : confirmingSession === session.id ? (
                <span className="flex items-center gap-2.5">
                  <span className="text-[12.5px]">Sign it out?</span>
                  <button
                    type="button"
                    disabled={revoke.isPending}
                    onClick={() =>
                      revoke.mutate(session.id, { onSuccess: () => setConfirmingSession(null) })
                    }
                    className="btn-accent focus-ring cursor-pointer px-3 py-1.5 text-[12.5px] disabled:cursor-default disabled:opacity-50"
                  >
                    {revoke.isPending ? 'Signing out…' : 'Yes, sign it out'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingSession(null)}
                    className="btn-quiet focus-ring cursor-pointer text-[12.5px]"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Sign out ${deviceLabel(session.device)}`}
                  onClick={() => setConfirmingSession(session.id)}
                  className="btn-quiet focus-ring cursor-pointer text-[11.5px]"
                  style={{ color: 'var(--dim)' }}
                >
                  Sign out
                </button>
              )}
            </div>
          ))}
        </div>

        {(sessions.data?.length ?? 0) > 1 && confirmingAll ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13px]">Sign out every session except this one?</span>
            <button
              type="button"
              disabled={revokeOthers.isPending}
              onClick={() =>
                revokeOthers.mutate(undefined, { onSuccess: () => setConfirmingAll(false) })
              }
              className="btn-accent focus-ring min-h-[38px] cursor-pointer px-4 py-2 text-[13.5px] disabled:cursor-default disabled:opacity-50"
            >
              {revokeOthers.isPending ? 'Signing out…' : 'Yes, sign them all out'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingAll(false)}
              className="btn-quiet focus-ring cursor-pointer text-[12.5px]"
            >
              Cancel
            </button>
          </div>
        ) : null}

        {revoke.isError || revokeOthers.isError ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            Could not sign that out. Try again in a moment.
          </p>
        ) : null}

        {/*
          Under the list, where the design puts it, and where it is read as a
          note about the button beside each row rather than as a description of
          the card. One entry per sign-in, not per stored row: rotation replaces
          the token and keeps the rows it replaced so that presenting one is
          detectable, so a list of rows would grow on every page load.
        */}
        <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
          Signing out a session ends it everywhere at once, including any copy of its cookie.
        </span>
      </div>
    </div>
  );
}

/** A name somebody will recognise later, from what the browser will admit to. */
const defaultName = (): string => {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/Macintosh/.test(agent)) return 'This Mac';
  if (/iPhone/.test(agent)) return 'This iPhone';
  if (/iPad/.test(agent)) return 'This iPad';
  if (/Windows/.test(agent)) return 'This PC';
  return 'This device';
};

/**
 * The sign-in wording, minus the two sentences that do not apply here.
 *
 * `passkeyMessage` tells somebody to sign in with their password instead, which
 * is right when they are locked out and wrong when they are already signed in
 * and adding a key. `no-passkey` also means something different here: not "you
 * have no key for this site" but "the prompt was dismissed, or this device
 * already holds a key for this account".
 */
const registerMessage = (failure: PasskeyFailure): string => {
  switch (failure.kind) {
    case 'unsupported':
      return 'This browser cannot create passkeys. Try a different browser, or a device with Touch ID or Windows Hello.';
    case 'no-passkey':
      return 'No passkey was created. The prompt was dismissed, or this device already has a key for this account.';
    case 'rejected':
      return 'That key was not accepted. Try again.';
    default:
      return passkeyMessage(failure);
  }
};
